import { Tooltip } from "@wix/design-system";
import { EditSmall } from "@wix/wix-ui-icons-common";
import cx from "classnames";
import React, { forwardRef, useContext, useEffect, useRef, useState } from "react";
import { ADD_CAPTION_BUTTON } from "./../../plugin-figure/data-hooks.js";
import { RicosContext } from "./../../ricos-context/index.js";
import classes from "./add-caption-button-scss.js";
const CaptionButtonContent = forwardRef(
  ({ onAddCaption, isIconOnly }, ref) => {
    const { t, isMobile } = useContext(RicosContext);
    const handleClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      onAddCaption?.();
    };
    return /* @__PURE__ */ React.createElement(
      "button",
      {
        ref,
        className: cx(classes.addCaptionButton, {
          [classes.mobile]: isMobile,
          [classes.withIcon]: isIconOnly,
          [classes.iconOnly]: isIconOnly
        }),
        onClick: handleClick,
        onMouseDown: (e) => {
          e.preventDefault();
          e.stopPropagation();
        },
        onTouchEnd: isMobile ? handleClick : void 0,
        "data-hook": ADD_CAPTION_BUTTON,
        type: "button",
        "aria-label": t("Figure_Caption_AddButton_AriaLabel")
      },
      /* @__PURE__ */ React.createElement(EditSmall, { className: classes.icon }),
      !isIconOnly && t("Figure_Caption_AddButton_Title")
    );
  }
);
const AddCaptionButton = ({
  onAddCaption,
  containerRef
}) => {
  const { t, jsdomMode } = useContext(RicosContext);
  const [buttonMode, setButtonMode] = useState(() => jsdomMode ? "full" : "hidden");
  const [dimensions, setDimensions] = useState(
    null
  );
  const buttonRef = useRef(null);
  const fullButtonRef = useRef(null);
  const iconOnlyButtonRef = useRef(null);
  useEffect(() => {
    if (!containerRef?.current || dimensions) {
      return;
    }
    const measureButtons = () => {
      if (!fullButtonRef.current || !iconOnlyButtonRef.current) {
        return;
      }
      const fullButtonWidth = fullButtonRef.current.offsetWidth;
      const iconButtonWidth = iconOnlyButtonRef.current.offsetWidth;
      setDimensions({ fullWidth: fullButtonWidth, iconWidth: iconButtonWidth });
    };
    measureButtons();
  }, [containerRef, dimensions]);
  useEffect(() => {
    if (!containerRef?.current || !dimensions || jsdomMode) {
      return;
    }
    const container = containerRef.current;
    const checkButtonFit = () => {
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;
      if (dimensions.fullWidth <= containerWidth && containerHeight >= 50) {
        setButtonMode("full");
      } else if (dimensions.iconWidth <= containerWidth && containerHeight >= 40) {
        setButtonMode("icon-only");
      } else {
        setButtonMode("hidden");
      }
    };
    checkButtonFit();
    const resizeObserver = new ResizeObserver(() => {
      checkButtonFit();
    });
    resizeObserver.observe(container);
    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef, dimensions]);
  return /* @__PURE__ */ React.createElement(
    "div",
    {
      className: classes.addCaptionButtonContainer,
      contentEditable: false,
      suppressContentEditableWarning: true
    },
    !dimensions && /* @__PURE__ */ React.createElement(
      "div",
      {
        style: { position: "absolute", visibility: "hidden", top: "-9999px" },
        "aria-hidden": "true",
        role: "presentation"
      },
      /* @__PURE__ */ React.createElement(CaptionButtonContent, { ref: fullButtonRef, isIconOnly: false }),
      /* @__PURE__ */ React.createElement(CaptionButtonContent, { ref: iconOnlyButtonRef, isIconOnly: true })
    ),
    (() => {
      switch (buttonMode) {
        case "full":
          return /* @__PURE__ */ React.createElement(
            CaptionButtonContent,
            {
              ref: buttonRef,
              onAddCaption,
              isIconOnly: false
            }
          );
        case "icon-only":
          return /* @__PURE__ */ React.createElement(Tooltip, { content: t("Figure_Caption_AddButton_Title"), size: "small" }, /* @__PURE__ */ React.createElement(CaptionButtonContent, { ref: buttonRef, onAddCaption, isIconOnly: true }));
        case "hidden":
          return null;
        default:
          return null;
      }
    })()
  );
};
export {
  AddCaptionButton
};
//# sourceMappingURL=add-caption-button.js.map
